import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import {
  Flex,
  LineChart,
  BarChart,
  DisplaySwitch,
  formatNumber,
  formatDatetime,
  CurrencyValue
} from '@blockanalitica/ui';
import { useMemo, useState, useCallback } from 'react';
import { ExtendedChartOptions } from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import { useParams } from 'react-router-dom';
import { useChartPeriod } from '@/hooks';
import { useTheme } from 'styled-components';

export default function HistoricChart() {
  const { walletAddress } = useParams();

  const theme = useTheme();

  const [activeDisplayOption, setActiveDisplayOption] = useState('balances');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod({
    options: { fill: false } as ExtendedChartOptions
  });

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  const displayOptions = [
    { key: 'balances', value: 'Balances' },
    { key: 'volume', value: 'Volume' }
  ];

  const [data, error] = useApi({
    url: `/arbitrum/psm3/depositors/${walletAddress}/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const datasets = useMemo(() => {
    switch (activeDisplayOption) {
      case 'volume':
        return [
          {
            xField: 'date',
            yField: 'deposit_volume',
            label: 'Deposit Volume',
            backgroundColor: theme.colors.chartUp
          },
          {
            xField: 'date',
            yField: 'withdraw_volume',
            label: 'Withdraw Volume',
            backgroundColor: theme.colors.chartDown
          }
        ];
      case 'balances':
        return [
          {
            label: 'USDS Balance',
            borderColor: 'rgba(255, 139, 91, 1)',
            xField: 'date',
            yField: 'usds_balance'
          },
          {
            label: 'sUSDS Balance',
            borderColor: 'rgba(0, 245, 170, 1)',
            xField: 'date',
            yField: 'susds_balance'
          },
          {
            label: 'USDC Balance',
            borderColor: 'rgba(100, 150, 250, 1)',
            xField: 'date',
            yField: 'usdc_balance'
          }
        ];
    }
  }, [activeDisplayOption, theme.colors]);

  const yScaleValueFormatter = (value) => {
    return formatNumber(Number(value));
  };
  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  const valueFormatter = useMemo(() => {
    function formatValue(value: string) {
      return (
        <CurrencyValue
          value={Number(value)}
          options={{
            currency: 'USD'
          }}
        />
      );
    }

    return formatValue;
  }, []);
  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />
        {activeDisplayOption === 'volume' ? (
          <BarChart
            key={activeDisplayOption}
            data={data.volume}
            datasets={datasets}
            valueFormatter={valueFormatter}
            yScaleValueFormatter={yScaleValueFormatter}
            subValueFormatter={subValueFormatter}
            timeFilter={timeFilter}
            config={config}
          />
        ) : (
          <LineChart
            key={activeDisplayOption}
            data={data.balances}
            valueFormatter={yScaleValueFormatter}
            datasets={datasets}
            yScaleValueFormatter={yScaleValueFormatter}
            timeFilter={timeFilter}
            config={config}
          />
        )}
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
